import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { Loader2, SearchX, Send, ShieldCheck, X, XCircle } from "lucide-react";
import { ROLE_LABEL, type AdminRow } from "@contractorhub/shared";
import { apiFetch, errorMessage } from "../../../lib/api";
import { adminAdminsQuery, meQuery } from "../../../lib/queries";
import {
  ActionMenu,
  Alert,
  Avatar,
  Button,
  Card,
  DataTable,
  EmptyState,
  Modal,
  PageHeader,
  SearchInput,
  Select,
  StatusBadge,
  actionMenuItemClass,
  type Column,
} from "../../../components/ui";
import { EditAdminDialog } from "../../../components/edit-dialogs";
import { InviteLinkPanel } from "../../../components/invite-link-panel";
import { NewAdminDialog } from "../../../components/new-admin-dialog";

export const Route = createFileRoute("/_authed/_admin/admins")({
  loader: ({ context }) => context.queryClient.ensureQueryData(adminAdminsQuery),
  component: AdminsPage,
});

/** A freshly re-sent invite link, held only until the admin dismisses it. */
type ReissuedInvite = {
  name: string;
  email: string;
  inviteLink: string;
  emailed: boolean;
  provisioned: boolean;
};

/**
 * The badge label, extracted from the column that used to compute it inline.
 * The filter and the badge have to agree on what "Revoked" means, and the only
 * way to guarantee that is for them to be the same function.
 */
function statusOf(admin: AdminRow): string {
  if (admin.status === "INVITED") {
    return admin.revokedInviteId ? "Revoked" : "Invited";
  }
  return admin.status === "ACTIVE" ? "Active" : "Inactive";
}

const STATUS_FILTERS = ["Active", "Invited", "Revoked", "Inactive"] as const;

function AdminsPage() {
  const { data } = useSuspenseQuery(adminAdminsQuery);
  // Who is signed in: their own row offers no destructive action. The API refuses
  // a self-change regardless — this only stops the button existing. Read from the
  // `me` cache every route guard has already filled, not from the route context,
  // where it is typed nullable for the anonymous routes.
  const { data: me } = useSuspenseQuery(meQuery);
  const [error, setError] = useState<string | null>(null);
  const [issued, setIssued] = useState<ReissuedInvite | null>(null);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");

  const active = data.admins.filter((a) => a.status === "ACTIVE").length;
  const filtersOn = Boolean(search || status);

  function clearFilters() {
    setSearch("");
    setStatus("");
  }

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return data.admins.filter((admin) => {
      if (
        needle &&
        ![admin.name, admin.email].some((field) => field.toLowerCase().includes(needle))
      ) {
        return false;
      }
      return !status || statusOf(admin) === status;
    });
  }, [data.admins, search, status]);

  const columns: Column<AdminRow>[] = [
    {
      key: "name",
      header: "Name",
      cell: (admin) => (
        <div className="flex items-center gap-3">
          <Avatar name={admin.name} />
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="truncate font-medium text-text">{admin.name}</span>
              {admin.id === me.user.id && (
                <span className="text-xs text-subtle">(you)</span>
              )}
            </div>
            <div className="truncate text-xs text-subtle sm:hidden">{admin.email}</div>
          </div>
        </div>
      ),
    },
    {
      key: "email",
      header: "Email",
      priority: "sm",
      cell: (admin) => <span className="text-muted">{admin.email}</span>,
    },
    {
      key: "role",
      header: "Role",
      cell: (admin) => <span className="text-muted">{ROLE_LABEL[admin.role]}</span>,
    },
    {
      key: "status",
      header: "Status",
      cell: (admin) => <StatusBadge status={statusOf(admin)} />,
    },
    {
      key: "actions",
      header: "",
      align: "right",
      cell: (admin) => (
        <AdminActions
          admin={admin}
          isSelf={admin.id === me.user.id}
          onError={setError}
          onInviteReissued={setIssued}
        />
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Admins"
        description={`${data.admins.length} admins · ${active} active`}
        actions={<NewAdminDialog />}
      />

      {error && (
        <Alert tone="error" className="mb-4">
          {error}
        </Alert>
      )}

      <Card className="overflow-hidden">
        <div className="flex flex-col gap-2 border-b border-line p-3 sm:flex-row sm:items-center">
          <div className="sm:w-72">
            <SearchInput
              aria-label="Search admins"
              placeholder="Search name or email"
              value={search}
              debounceMs={0}
              onChange={setSearch}
            />
          </div>
          <Select
            aria-label="Filter by status"
            className="sm:w-40"
            value={status}
            onChange={(event) => setStatus(event.target.value)}
          >
            <option value="">All statuses</option>
            {STATUS_FILTERS.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </Select>
          {filtersOn && (
            <Button variant="ghost" size="sm" icon={X} onClick={clearFilters}>
              Clear
            </Button>
          )}
        </div>

        <DataTable
          columns={columns}
          rows={filtered}
          getRowKey={(admin) => admin.id}
          caption="Administrators"
          empty={
            filtersOn ? (
              <EmptyState
                icon={SearchX}
                title="No matches"
                description={
                  search
                    ? `No admin matches “${search.trim()}” with the current filters.`
                    : "No admin matches the current filters."
                }
                action={
                  <Button variant="secondary" icon={X} onClick={clearFilters}>
                    Clear filters
                  </Button>
                }
              />
            ) : (
              <EmptyState
                icon={ShieldCheck}
                title="No admins"
                description="Invite an admin to approve timesheets, or a user admin to manage contractors."
              />
            )
          }
        />
      </Card>

      <p className="mt-4 text-xs text-subtle">
        An admin can approve timesheets, see rates and payments, and manage contractors,
        clients and other admins. A user admin can only add and edit contractors and assign
        them to existing projects. You can&apos;t change your own access, and the last
        active admin can&apos;t be deactivated or made a user admin.
      </p>

      {/* One modal for the whole table: only ever one re-sent link at a time. */}
      <Modal open={issued !== null} onClose={() => setIssued(null)} title="Invite re-sent">
        {issued && (
          <InviteLinkPanel
            name={issued.name}
            email={issued.email}
            inviteLink={issued.inviteLink}
            emailed={issued.emailed}
            provisioned={issued.provisioned}
            resendFrom="admin list"
            onDone={() => setIssued(null)}
          />
        )}
      </Modal>
    </div>
  );
}

function AdminActions({
  admin,
  isSelf,
  onError,
  onInviteReissued,
}: {
  admin: AdminRow;
  isSelf: boolean;
  onError: (message: string | null) => void;
  onInviteReissued: (issued: ReissuedInvite) => void;
}) {
  const queryClient = useQueryClient();
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["admin"] });

  const setStatus = useMutation({
    mutationFn: (status: "ACTIVE" | "INACTIVE") =>
      apiFetch("POST /api/manage/admins/{id}/status", {
        params: { id: admin.id },
        body: { status },
      }),
    onSuccess: async () => {
      onError(null);
      await refresh();
    },
    onError: (err) => onError(errorMessage(err)),
  });

  const resend = useMutation({
    mutationFn: (inviteId: string) =>
      apiFetch("POST /api/manage/invites/{id}/resend", { params: { id: inviteId } }),
    onSuccess: async ({ inviteLink, emailed, provisioned }) => {
      onError(null);
      onInviteReissued({ name: admin.name, email: admin.email, inviteLink, emailed, provisioned });
      await refresh();
    },
    onError: (err) => onError(errorMessage(err)),
  });

  const revoke = useMutation({
    mutationFn: (inviteId: string) =>
      apiFetch("POST /api/manage/invites/{id}/revoke", { params: { id: inviteId } }),
    onSuccess: async () => {
      onError(null);
      await refresh();
    },
    onError: (err) => onError(errorMessage(err)),
  });

  const isActive = admin.status === "ACTIVE";
  const isInvited = admin.status === "INVITED";
  const busy = setStatus.isPending || resend.isPending || revoke.isPending;

  return (
    <div className="flex justify-end">
      <ActionMenu label={`Actions for ${admin.name}`}>
          <EditAdminDialog admin={admin} isSelf={isSelf} buttonClassName={actionMenuItemClass} />

          {isInvited && admin.pendingInviteId ? (
            <>
              <button
                className={actionMenuItemClass}
                disabled={busy}
                onClick={() => resend.mutate(admin.pendingInviteId!)}
                title="Generate a fresh invite link (the old one stops working)"
              >
                {resend.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                ) : (
                  <Send className="h-4 w-4" aria-hidden="true" />
                )}
                Resend invite
              </button>
              <button
                className={actionMenuItemClass}
                disabled={busy}
                onClick={() => revoke.mutate(admin.pendingInviteId!)}
                title="Cancel this invite so the link can no longer be used"
              >
                {revoke.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                ) : (
                  <XCircle className="h-4 w-4" aria-hidden="true" />
                )}
                Revoke
              </button>
            </>
          ) : isInvited && admin.revokedInviteId ? (
            // A revoked invitee's only way back is a fresh invite: their email is
            // taken, so they can never be created again. Resend re-opens the invite.
            <button
              className={actionMenuItemClass}
              disabled={busy}
              onClick={() => resend.mutate(admin.revokedInviteId!)}
              title="Issue a fresh invite (the revoked one is re-opened with a new link)"
            >
              {resend.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                <Send className="h-4 w-4" aria-hidden="true" />
              )}
              Re-invite
            </button>
          ) : isSelf ? null : (
            <button
              className={actionMenuItemClass}
              disabled={busy}
              onClick={() => setStatus.mutate(isActive ? "INACTIVE" : "ACTIVE")}
              title={
                isActive
                  ? "Deactivating cuts off access on their next request"
                  : "Restores access"
              }
            >
              {setStatus.isPending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
              {isActive ? "Deactivate" : "Reactivate"}
            </button>
      )}
      </ActionMenu>
    </div>
  );
}
